import DatePicker from 'components/date-picker';
import LoadingSpinner from 'components/loading-spinner';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedDate, useIntl } from 'react-intl';
import { MarketId } from 'types/market-id.enum';
import { CreateOrderFormValues } from 'views/order-feed/features/my-supply-plan/types/create-order-form-values.type';
import {
  getOrderDateParts,
  getOrderDateValue,
  isOrderDateInShippingSchedule,
} from 'views/order-feed/features/my-supply-plan/utils/order-utils';
import { DispatchSchedule } from 'views/order-feed/types/shipping-schedule.type';

type Props = {
  shippingSchedule: DispatchSchedule | undefined;
  isLoading: boolean;
};

type OrderDateProps = {
  label: string;
  shippingSchedule: DispatchSchedule;
};

const OrderDateSelect: React.FC<OrderDateProps> = ({ label, shippingSchedule }) => {
  const intl = useIntl();
  const {
    control,
    setValue,
    formState: { defaultValues },
  } = useFormContext<CreateOrderFormValues>();
  const selectedDateValue = useWatch({ control, name: 'requestedDispatchDate' });
  const defaultScheduleValue = defaultValues?.requestedDispatchDate ?? '';
  const resolvedMinDate = dayjs(shippingSchedule.earliestPossibleDispatchDate) || dayjs().add(10, 'days');

  const { dispatchDateData } = shippingSchedule;

  return (
    <Select
      dark
      fullWidth
      label={label}
      controller={{
        name: 'requestedDispatchDate',
        control,
        rules: {
          required: intl.formatMessage({ id: 'form.rules.required' }),
          validate: {
            inSchedule: (dateValue: string) => isOrderDateInShippingSchedule(shippingSchedule, dateValue),
            minimumDate: (dateValue: string) =>
              dateValue === defaultScheduleValue ||
              dayjs(getOrderDateParts(dateValue).requestedDispatchDate).isSameOrAfter(resolvedMinDate, 'day'),
          },
        },
      }}
      onChange={(event) => {
        const { plantId, vessel } = getOrderDateParts(String(event.target.value));

        setValue('plantId', plantId || '');
        setValue('vessel', vessel || '');
      }}>
      {dispatchDateData
        .filter(
          (schedule) =>
            dayjs(schedule.dispatchDate).isSameOrAfter(resolvedMinDate, 'day') || getOrderDateValue(schedule) === defaultScheduleValue
        )
        .map((schedule, idx) => {
          const value = getOrderDateValue(schedule);

          return (
            <RadioSelectItem
              key={`${getOrderDateValue(schedule)}_${idx}`}
              selected={value === selectedDateValue}
              value={value}
              sx={{ flexDirection: 'column', alignItems: 'flex-start', height: 'auto' }}>
              <FormattedDate value={schedule.dispatchDate} day="2-digit" month="short" year="numeric" />
              <small>
                {schedule.factory} / {schedule.vessel}
              </small>
            </RadioSelectItem>
          );
        })}
    </Select>
  );
};

const OrderDatePicker: React.FC<OrderDateProps> = ({ label, shippingSchedule }) => {
  const intl = useIntl();
  const {
    control,
    setValue,
    formState: { defaultValues },
  } = useFormContext<CreateOrderFormValues>();
  const defaultScheduleValue = defaultValues?.requestedDispatchDate ?? '';

  const defaultDispatchDate = getOrderDateParts(defaultScheduleValue).requestedDispatchDate;
  const resolvedMinDate = dayjs(shippingSchedule.earliestPossibleDispatchDate) || dayjs().add(10, 'days');
  const isDefaultDateBeforeMin = dayjs(defaultDispatchDate).isBefore(resolvedMinDate, 'day');

  return (
    <DatePicker
      minDate={isDefaultDateBeforeMin ? defaultDispatchDate : resolvedMinDate}
      shouldDisableDate={(date) =>
        isDefaultDateBeforeMin &&
        dayjs(date).isAfter(dayjs(defaultDispatchDate), 'day') &&
        dayjs(date).isBefore(resolvedMinDate, 'day')
      }
      dark
      fullWidth
      label={label}
      controller={{
        name: 'requestedDispatchDate',
        control,
        rules: {
          required: intl.formatMessage({ id: 'form.rules.required' }),
          validate: {
            minimumDate: (dateValue: string) =>
              dateValue === defaultDispatchDate || dayjs(dateValue).isSameOrAfter(resolvedMinDate, 'day'),
          },
        },
      }}
      onChange={() => {
        setValue('plantId', '');
        setValue('vessel', '');
      }}
    />
  );
};

const SelectOrderDate: React.FC<Props> = ({ shippingSchedule, isLoading }) => {
  const { control, trigger } = useFormContext<CreateOrderFormValues>();
  const selectedDateValue = useWatch({ control, name: 'requestedDispatchDate' });
  const watchOrderLines = useWatch({ control, name: 'orderLines' });
  const intl = useIntl();
  const { account } = useAccountState();
  const isChileMarket = account.company?.marketId === MarketId.Chile;
  const resolvedLabel = intl.formatMessage({
    id: isChileMarket ? 'orderFeed.ofdMySupplyPlan.orders.list.deliveryDate' : 'orderFeed.ofdMySupplyPlan.orders.dispatchDate',
  });

  useEffect(() => {
    watchOrderLines?.forEach((line, idx) => {
      if (line.itemNumber) trigger(`orderLines.${idx}.itemNumber`);
    });
  }, [selectedDateValue, trigger]);

  if (isLoading) return <LoadingSpinner small />;

  if (shippingSchedule) {
    if (shippingSchedule?.dispatchDateData.length) {
      return <OrderDateSelect label={resolvedLabel} shippingSchedule={shippingSchedule} />;
    }

    return <OrderDatePicker label={resolvedLabel} shippingSchedule={shippingSchedule} />;
  }
  return null;
};

export default SelectOrderDate;
